"use client";

import { Logo } from "@/components/images/logo";
import type { BlockConfig } from "@/lib/registry/types";
import { motion } from "framer-motion";
import { AutocompleteSearch } from "./autocomplete-search";

interface HeadingSectionProps {
  components: BlockConfig[];
}

const h1Variants = {
  hidden: {},
  visible: {
    transition: {
      // No stagger needed if we use delays on children
    },
  },
};

/*
 * Animation variants for the "Ui" part of the title.
 * It fades in, pauses slightly, then slides to its final position.
 */
const uiVariants = {
  hidden: { opacity: 0, x: 60 }, // Start invisible and slightly offset to simulate centering
  visible: {
    opacity: [0, 1, 1, 1], // Keyframes: hidden -> visible (0.5s) -> stay visible
    x: [60, 60, 60, 0], // Keyframes: start offset -> stay offset (until 1.0s) -> move to final position (1.0s -> 1.5s)
    transition: {
      duration: 1.5, // Total animation duration for "Ui"
      times: [0, 0.33, 0.66, 1], // Timing: 0s (start), 0.5s (fade-in done), 1.0s (pause/crossfade done), 1.5s (slide done)
      ease: "easeInOut",
      // Opacity fade-in handled by main keyframe
      // X slide handled by main keyframe
    },
  },
};

/*
 * Animation variants for the regular "U".
 * It's visible initially (inheriting parent fade-in) and fades out during the pause.
 */
const regularUVariants = {
  visible: {
    opacity: [1, 1, 1, 0], // Keyframes: Opacity at 0s, 0.5s, 1.0s, 1.5s
    transition: {
      duration: 2, // Match parent duration
      times: [0, 0.33, 0.66, 1], // Align with parent timing: start fade out at 0.5s, end at 1.0s
      ease: "linear",
    },
  },
  // No hidden state needed, parent handles initial visibility
};

/*
 * Animation variants for the overlined "U".
 * It fades in during the pause.
 */
const overlinedUVariants = {
  visible: {
    opacity: [0, 0, 0, 1], // Keyframes: Opacity at 0s, 0.5s, 1.0s, 1.5s
    transition: {
      duration: 2, // Match parent duration
      times: [0, 0.33, 0.66, 1], // Align with parent timing: start fade in at 0.5s, end at 1.0s
      ease: "linear",
    },
  },
  // No hidden state needed, parent handles initial visibility
};

/*
 * Animation variants for the "brary" part of the title.
 * It fades in after a delay, coinciding with the "Ui" part sliding.
 */
const braryVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delay: 1.5, // Start fading in after 0.5s (when "Ui" starts sliding)
      duration: 0.5, // Fade in over 0.5s
    },
  },
};

export function HeadingSection({ components }: HeadingSectionProps) {
  return (
    <div className="flex justify-center w-full mb-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-3xl w-full text-center space-y-6 relative z-50 pt-12 md:pt-20 px-4 mx-auto"
      >
        {/* <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
          className="flex justify-center mb-4"
        >
          <Logo className="h-12 w-12 text-primary" />
        </motion.div> */}

        {/* Animated H1 Title */}
        <motion.h1
          className="text-4xl md:text-5xl font-medium tracking-tight flex justify-center items-baseline min-h-[60px] md:min-h-[72px]"
          variants={h1Variants}
          initial="hidden"
          animate="visible"
        >
          {/* Animated "Ui" part */}
          <motion.span variants={uiVariants} className="inline-block relative">
            <span className="">
              <span className="relative inline-block">
                {" "}
                {/* Ensure relative positioning context for absolute U */}
                {/* Regular U fades out */}
                <motion.span
                  variants={regularUVariants}
                  className="absolute left-0 top-0"
                >
                  U
                </motion.span>
                {/* Overlined U fades in */}
                <motion.span
                  variants={overlinedUVariants}
                  className="overline inline-block" /* Use inline-block to maintain space */
                >
                  U
                </motion.span>
              </span>
              i
            </span>
          </motion.span>
          {/* Animated "brary" part */}
          <motion.span variants={braryVariants} className="inline-block">
            brary
          </motion.span>
        </motion.h1>

        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          Open-source UI components for your projects.
        </p>

        <motion.p
          className="text-sm text-muted-foreground"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }} // Slightly different delay
        >
          {components.length} components and counting.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <AutocompleteSearch components={components} />
        </motion.div>
      </motion.div>
    </div>
  );
}
